import React, { useState, useEffect } from "react";
import {
  Box, Card, CardContent, Typography,
  TextField, Select, MenuItem, Button, Stack, CircularProgress
} from "@mui/material";
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useNavigate } from "react-router-dom";
import axios from "axios";
import SignupStep2 from "./SignupStep2";

const Signup = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);

  // 0단계 상태
  const [inputSignupCode, setInputSignupCode] = useState("");
  const [signupCodeError, setSignupCodeError] = useState('');
  const [isValidatingCode, setIsValidatingCode] = useState(false);

  // 1단계 상태
  const [username, setUsername] = useState("");
  const [usernameError, setUsernameError] = useState('');
  const [usernameChecked, setUsernameChecked] = useState(false);
  const [checkingUsername, setCheckingUsername] = useState(false);
  const [password1, setPassword1] = useState("");
  const [password2, setPassword2] = useState("");
  const [gender, setGender] = useState("");
  const [preference, setPreference] = useState("");
  const [regionCode, setRegionCode] = useState("");
  //const [signupCode, setSignupCode] = useState("");
  const [verifiedSignupCode, setVerifiedSignupCode] = useState("");
  const [birthYear, setBirthYear] = useState("");
  //const currentYear = new Date().getFullYear();

  // 2단계 상태
  const [selectedTraits, setSelectedTraits] = useState([]);
  const [offlineStatus, setOfflineStatus] = useState("");
  const [recruitMain, setRecruitMain] = useState("");
  const [recruitSub, setRecruitSub] = useState("");
  const [recruitNote, setRecruitNote] = useState("");
  const [recruitFlag, setRecruitFlag] = useState("");
  const [recruitCustom, setRecruitCustom] = useState("");

  // ✨ 참여코드 확인 함수 (새로 추가)
  const handleVerifyCode = async () => {
    if (!inputSignupCode) {
      setSignupCodeError("참여코드를 입력해주세요.");
      return;
    }
    setIsValidatingCode(true);
    setSignupCodeError('');
    try {
      const { data } = await axios.post(
        "/api/accounts/verify-code/",
        { signup_code: inputSignupCode },
        { withCredentials: true }
      );
      if (data.success) {
        setVerifiedSignupCode(inputSignupCode); // ✨ 검증된 코드 저장
        setStep(1); // ✨ 성공 시 다음 단계로
      } else {
        // 백엔드에서 success: false 로 응답한 경우 (거의 발생 안 함)
        setSignupCodeError(data.message || "코드 확인 중 오류가 발생했습니다.");
      }
    } catch (err) {
      // 4xx, 5xx 에러
      setSignupCodeError(err.response?.data?.message || "참여코드가 올바르지 않거나 서버 오류입니다.");
    } finally {
      setIsValidatingCode(false);
    }
  };



  // ✅ 멜/펨 성향 목록 확장
  const preferenceOptions = {
    멜: ["MD", "MS", "MSW", "MSD", "MSS"],
    펨: ["FD", "FS", "FSW", "FSD", "FSS"],
  };

  const yearOptions = Array.from({ length: 21 }, (_, i) => 2010 - i);

  useEffect(() => {
    axios.get("/api/accounts/csrf/", { withCredentials: true });
  }, []);

  const checkUsername = () => {
    if (!username) return;
    setCheckingUsername(true);
    axios.get(`/api/public/check-username/?username=${username}`)
      .then(res => {
        const exists = res.data.exists;
        setUsernameError(exists ? '이미 사용 중인 닉네임입니다.' : '사용 가능한 닉네임입니다.');
        setUsernameChecked(!exists);
      })
      .catch(() => {
        setUsernameError('중복 확인 실패');
        setUsernameChecked(false);
      })
      .finally(() => setCheckingUsername(false));
  };

  const handleNextStep = (e) => {
    e.preventDefault();
    // ✨ signupCode 필드 제거
    if (!username || !password1 || !password2 || !gender || !preference || !regionCode || !birthYear) {
      alert("모든 항목을 입력해주세요.");
      return;
    }
    if (!usernameChecked) {
      alert("닉네임 중복확인을 완료해주세요.");
      return;
    }
    if (password1 !== password2) {
      alert("비밀번호가 일치하지 않습니다.");
      return;
    }
    setStep(2);
  };

  const handleFinalSubmit = async () => {
    try {
      const payload = {
        username,
        password1,
        password2,
        gender,
        preference,
        region_code: regionCode,
        signup_code: verifiedSignupCode,
        birth_year: birthYear,
        detailed_traits: selectedTraits,
        offline_status: offlineStatus,
        recruit_main: recruitMain,
        recruit_sub: recruitSub,
        recruit_note:
          recruitMain === '구인중' && recruitSub === '기타'
            ? recruitCustom           // ✅ 기타(직접작성)일 때 이 값 전송
            : recruitNote,            // 그 외에는 기존 값
        recruit_flag: recruitFlag,
      };

      const { data } = await axios.post(
        "/api/accounts/signup/",
        payload,
        { withCredentials: true }
      );

      alert(data.message);
      if (data.success) navigate("/");
    } catch (err) {
      alert(err.response?.data?.message || "회원가입 실패");
    }
  };

  return (
    <Box sx={{
      display: 'flex', justifyContent: 'center', alignItems: 'center',
      minHeight: '100vh', backgroundColor: '#f5f5f5'
    }}>
      <Card sx={{ width: 500, p: 3 }}>
        <CardContent>
          <Typography
            variant="h4"
            sx={{
              fontFamily: `'Dancing Script', cursive`,
              fontWeight: 'bold',
              textAlign: 'center',
              mb: 3
            }}
          >
            SanDle Signup
          </Typography>

          {/* ✨ Step 0 UI 추가 */}
          {step === 0 && (
            <Stack spacing={2} sx={{ mt: 3 }}>
              <TextField
                label="참여코드"
                value={inputSignupCode} // ✨ 새 상태 사용
                onChange={(e) => {
                  setInputSignupCode(e.target.value);
                  setSignupCodeError('');
                }}
                fullWidth
                error={!!signupCodeError}
                helperText={signupCodeError || ' '}
                disabled={isValidatingCode}
              />
              <Button
                variant="contained"
                fullWidth
                onClick={handleVerifyCode} // ✨ 새 함수 연결
                disabled={isValidatingCode}
              >
                {isValidatingCode ? <CircularProgress size={24} color="inherit" /> : "참여코드 확인"}
              </Button>
              <Button fullWidth onClick={() => navigate("/login")} disabled={isValidatingCode}>
                로그인 페이지로 돌아가기
              </Button>
            </Stack>
          )}

          {step === 1 && (
            <form onSubmit={handleNextStep}>
              <Stack spacing={2}>
                <TextField
                  label="닉네임 (아이디)"
                  value={username}
                  onChange={(e) => {
                    setUsername(e.target.value);
                    setUsernameError('');
                    setUsernameChecked(false);
                  }}
                  fullWidth
                  error={!!usernameError && usernameError !== '사용 가능한 닉네임입니다.'}
                  helperText={usernameError || ' '}
                  InputProps={{
                    endAdornment: (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {usernameChecked && (
                          <CheckCircleIcon color="success" fontSize="small" />
                        )}
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={checkUsername}
                          disabled={checkingUsername}
                          sx={{ whiteSpace: 'nowrap', fontSize: '0.75rem', px: 1.5 }}
                        >
                          {checkingUsername ? '확인중...' : '중복확인'}
                        </Button>
                      </Box>
                    )
                  }}
                />

                <TextField
                  label="비밀번호"
                  type="password"
                  value={password1}
                  onChange={(e) => setPassword1(e.target.value)}
                  fullWidth
                />
                <TextField
                  label="비밀번호 확인"
                  type="password"
                  value={password2}
                  onChange={(e) => setPassword2(e.target.value)}
                  fullWidth
                />

                <Select
                  value={gender}
                  onChange={(e) => {
                    setGender(e.target.value);
                    setPreference("");
                  }}
                  displayEmpty
                  fullWidth
                >
                  <MenuItem value="">성별 선택</MenuItem>
                  <MenuItem value="멜">멜</MenuItem>
                  <MenuItem value="펨">펨</MenuItem>
                </Select>

                <Select
                  value={preference}
                  onChange={(e) => setPreference(e.target.value)}
                  displayEmpty
                  fullWidth
                  disabled={!gender}
                >
                  <MenuItem value="">메인 성향 선택</MenuItem>
                  {(preferenceOptions[gender] || []).map((opt) => (
                    <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                  ))}
                </Select>

                <Select
                  value={birthYear}
                  onChange={(e) => setBirthYear(e.target.value)}
                  displayEmpty
                  fullWidth
                >
                  <MenuItem value="">출생년도 선택</MenuItem>
                  {yearOptions.map((year) => (
                    <MenuItem key={year} value={year}>
                      {year}년생
                    </MenuItem>
                  ))}
                </Select>

                <TextField
                  label="지역번호 (예: 02, 031)"
                  value={regionCode}
                  onChange={(e) => setRegionCode(e.target.value)}
                  fullWidth
                />

                <Button type="submit" variant="contained" fullWidth>
                  다음 단계
                </Button>
                <Button fullWidth onClick={() => navigate("/login")}>
                  로그인 페이지로 돌아가기
                </Button>
              </Stack>
            </form>
          )}

          {step === 2 && (
            <>
              <SignupStep2
                selectedTraits={selectedTraits}
                setSelectedTraits={setSelectedTraits}
                recruitMain={recruitMain}
                setRecruitMain={setRecruitMain}
                recruitSub={recruitSub}
                setRecruitSub={setRecruitSub}
                recruitNote={recruitNote}
                setRecruitNote={setRecruitNote}
                recruitFlag={recruitFlag}
                setRecruitFlag={setRecruitFlag}
                offlineStatus={offlineStatus}
                setOfflineStatus={setOfflineStatus}
                recruitCustom={recruitCustom}
                setRecruitCustom={setRecruitCustom}
              />

              <Stack spacing={2} sx={{ mt: 3 }}>
                <Button variant="contained" fullWidth onClick={handleFinalSubmit}>
                  회원가입 완료
                </Button>
                <Button fullWidth onClick={() => setStep(1)}>
                  이전 단계로 돌아가기
                </Button>
              </Stack>
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default Signup;
